'use client'

import Link from 'next/link'
import { Github, Linkedin, Twitter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useState } from 'react'

export default function Footer() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')
    
    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })
      
      if (response.ok) {
        setStatus('success')
        setEmail('')
      } else {
        setStatus('error')
      }
    } catch (error) {
      setStatus('error')
      console.error('Newsletter subscription error:', error)
    }
  }
  
  return (
    <footer className="border-t border-border bg-background">
      <div className="container px-4 py-12 md:py-16 lg:py-20">
        <div className="xl:grid xl:grid-cols-5 xl:gap-8">
          <div className="xl:col-span-2">
            <div className="space-y-5">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
                  <span className="text-sm font-bold text-primary-foreground">FT</span>
                </div>
                <span className="text-lg font-semibold">FinTech Toronto</span>
              </div>
              <p className="max-w-sm text-sm text-muted-foreground">
                Toronto's hub for FinTech x AI innovation. Discover events, articles, and connect with a community of innovators driving global FinTech impact.
              </p>
              
              {/* Newsletter Subscription */}
              <div className="pt-4 pb-2">
                <h3 className="text-sm font-semibold mb-2">Subscribe to our newsletter</h3>
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="flex-1"
                    disabled={status === 'loading'}
                    aria-label="Email for newsletter"
                  />
                  <Button type="submit" size="sm" disabled={status === 'loading'}>
                    {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
                  </Button>
                </form>
                {status === 'success' && (
                  <p className="text-xs text-green-600 mt-1">Thanks for subscribing!</p>
                )}
                {status === 'error' && (
                  <p className="text-xs text-red-600 mt-1">Subscription failed. <Link href="/newsletter" className="underline">Try here</Link>.</p>
                )}
              </div>
              
              <div className="flex space-x-4">
                <a
                  href="https://twitter.com/fintechtoronto"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter className="h-5 w-5" />
                </a>
                <a
                  href="https://linkedin.com/company/fintechtoronto"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
                <a
                  href="https://github.com/fintechtoronto"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="GitHub"
                >
                  <Github className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-8 md:grid-cols-3 xl:col-span-3 xl:mt-0">
            <div>
              <h3 className="text-sm font-semibold">Resources</h3>
              <ul className="mt-4 space-y-3">
                <li>
                  <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link href="/events" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Events
                  </Link>
                </li>
                <li>
                  <Link href="/series" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Series
                  </Link>
                </li>
                <li>
                  <Link href="/newsletter" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Newsletter
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold">Community</h3>
              <ul className="mt-4 space-y-3">
                <li>
                  <Link href="/contribute" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Contribute
                  </Link>
                </li>
                <li>
                  <Link href="/code-of-conduct" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Code of Conduct
                  </Link>
                </li>
                <li>
                  <Link href="/privacy-policy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link href="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold">About</h3>
              <ul className="mt-4 space-y-3">
                <li>
                  <Link href="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link href="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Contact
                  </Link>
                </li>
                <li>
                  <Link href="https://lu.ma/fintechto" className="text-sm text-muted-foreground hover:text-foreground transition-colors" target="_blank" rel="noopener noreferrer">
                    Submit an Event
                  </Link>
                </li>
                <li>
                  <Link href="/auth/register" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Join Community
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-border">
          <p className="text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} FinTech Toronto - Toronto's FinTech x AI Community Hub. All rights reserved.
            <br />
            <span className="mt-1 inline-block">
              Built by <a 
                href="https://www.linkedin.com/in/karanbalaji/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline transition-colors"
              >
                Karan Balaji
              </a>
            </span>
          </p>
        </div>
      </div>
    </footer>
  )
} 